/**
 * Invalidates reference-data React Query cache when the user changes language.
 * Ensures countries, passport types, master data, and cancellation reasons refetch
 * with the new x-lang header without invalidating session/user queries.
 */

import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useBoundStore } from "@/stores";
import { authKeys } from "@/features/auth/api/queries";
import { masterDataKeys } from "@/features/book-appointments/api/master-data.queries";
import { manageAppointmentsKeys } from "@/features/manage-appointments/api/manage-appointments.queries";

export function ReferenceDataLanguageInvalidator() {
  const queryClient = useQueryClient();
  const currentLanguage = useBoundStore.use.currentLanguage();
  const previousLanguageRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const previousLanguage = previousLanguageRef.current;
    previousLanguageRef.current = currentLanguage;

    // Only invalidate when language actually changed (skip initial mount)
    if (previousLanguage !== undefined && previousLanguage !== currentLanguage) {
      queryClient.invalidateQueries({ queryKey: authKeys.countries() });
      queryClient.invalidateQueries({ queryKey: authKeys.passportTypes() });
      queryClient.invalidateQueries({ queryKey: masterDataKeys.all });
      queryClient.invalidateQueries({ queryKey: manageAppointmentsKeys.cancelReasons() });
    }
  }, [currentLanguage, queryClient]);

  return null;
}
